import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { defineMessages } from '../../i18n/define';
import { UNIT_LABELS, useT } from '../../i18n/hooks';
import { PageHeader } from '../ui/PageHeader';
import { Card, Grid, Stack } from '../ui/Card';
import { KpiCard } from '../ui/KpiCard';
import { DoneSplit } from '../ui/DoneSplit';
import { Disclosure } from '../ui/Disclosure';
import { DataTable, type Column } from '../ui/DataTable';
import { Badge } from '../ui/Badge';
import { MapWidget } from '../map/MapWidget';
import { DataGate } from './parts';
import { SkeletonList } from '../ui/Skeleton';
import { usePageTrail } from '../shell/ShellContext';
import { useTrackData } from '../data/useTrackData';
import type { DataState } from '../data/useTrackData';
import { FILES } from '../data/loader';
import { useAnalysisContext } from '../../analysis/context';
import {
  massifFarmsSet,
  scopeProgressSet,
  type ScopeProgressFile,
} from '../data-source/api/cards';
import { countOf, cycleTitle, ha, haUnit, num, WORDS } from '../lib/format';
import { DENSITY } from '../lib/levels';
import type { ContourMeta, Crop, Cycle, Farm, Massif } from '../data/types';
import './pages.css';

/**
 * Ход кампаний массива — ГОТОВЫЙ агрегат сервера по срезу «цикл × массив»
 * (`data-source/api/cards.ts`). Прежде экран брал журнал обязательств цикла
 * целиком и складывал его в браузере по контурам массива; на каноне такого
 * журнала нет и быть не должно — четыре числа считает сервер. Циклы тоже
 * приходят из среза, а не зашиты парой прототипа: у канона ключ цикла — пара
 * «метка сезона × культура», и зашитое имя не сошлось бы ни с чем.
 */
const m = defineMessages({
  levelDistrict: { id: 'massif.detail.trail.district', defaultMessage: 'Район', description: 'Уровень в цепочке навигации над заголовком: район' },
  districtName: { id: 'massif.detail.trail.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в цепочке навигации; географическое имя — транслитерация' },
  levelMassif: { id: 'massif.detail.trail.massif', defaultMessage: 'Массив', description: 'Уровень в цепочке навигации над заголовком: массив' },
  colFarm: { id: 'massif.detail.col.farm', defaultMessage: 'Хозяйство', description: 'Колонка таблицы хозяйств массива: название хозяйства' },
  colDirection: { id: 'massif.detail.col.direction', defaultMessage: 'Направление', description: 'Колонка таблицы хозяйств массива: направление хозяйства (хлопок, зерно…)' },
  colContours: { id: 'massif.detail.col.contours', defaultMessage: 'Контуры', description: 'Колонка таблицы хозяйств массива: число контуров хозяйства' },
  colLand: { id: 'massif.detail.col.land', defaultMessage: 'Земля', description: 'Колонка таблицы хозяйств массива: земля хозяйства; единица «га» в шапке рядом' },
  titleNamed: { id: 'massif.detail.title.named', defaultMessage: 'Массив {name}', description: 'Заголовок страницы массива; {name} — название массива' },
  titleFallback: { id: 'massif.detail.title.fallback', defaultMessage: 'Массив {id}', description: 'Заголовок страницы массива, пока название не прочитано; {id} — ключ массива из адреса' },
  purpose: { id: 'massif.detail.purpose', defaultMessage: 'Контуры и хозяйства этого массива. Величины считаются в его собственном знаменателе — без сравнения с районом и без рейтинга массивов между собой.', description: 'Подзаголовок страницы массива' },
  purposeMissing: { id: 'massif.detail.purposeMissing', defaultMessage: 'Массив в срезе не найден — проверьте адрес или трек данных.', description: 'Подзаголовок страницы массива, когда массив по адресу не найден' },
  kpiContours: { id: 'massif.detail.kpi.contours', defaultMessage: 'Контуры', description: 'Подпись KPI: число контуров массива' },
  kpiContoursNote: { id: 'massif.detail.kpi.contoursNote', defaultMessage: '{count} в реестре среза', description: 'Подпись под KPI «Контуры»; {count} — число контуров в реестре, число строкой' },
  kpiContoursNoteMissing: { id: 'massif.detail.kpi.contoursNoteMissing', defaultMessage: 'реестр контуров не прочитан', description: 'Подпись под KPI «Контуры», когда реестр контуров не загрузился' },
  kpiFarms: { id: 'massif.detail.kpi.farms', defaultMessage: 'Хозяйства', description: 'Подпись KPI: число хозяйств массива' },
  kpiFarmsNote: {
    id: 'massif.detail.kpi.farmsNote',
    defaultMessage: 'с любой долей земли в массиве; всех землепользователей по реестру массива — {count}, включая субъектов вне списка хозяйств',
    description: 'Подпись под KPI «Хозяйства»; {count} — число землепользователей по реестру массива, число строкой',
  },
  kpiFarmsNoteShort: { id: 'massif.detail.kpi.farmsNoteShort', defaultMessage: 'с любой долей земли в массиве', description: 'Подпись под KPI «Хозяйства», когда массив не прочитан' },
  kpiLand: { id: 'massif.detail.kpi.land', defaultMessage: 'Земля массива', description: 'Подпись KPI: земля массива в гектарах' },
  kpiLandNote: { id: 'massif.detail.kpi.landNote', defaultMessage: 'по реестру массивов', description: 'Подпись под KPI «Земля массива»' },
  kpiGeometry: { id: 'massif.detail.kpi.geometry', defaultMessage: 'С геометрией', description: 'Подпись KPI: у скольких контуров массива есть полигон' },
  kpiGeometryNote: {
    id: 'massif.detail.kpi.geometryNote',
    defaultMessage: 'из {count, plural, one {# контура} other {# контуров}} массива',
    description: 'Подпись под KPI «С геометрией»; {count} — число контуров массива',
  },
  campaignsTitle: { id: 'massif.detail.campaigns.title', defaultMessage: 'Идущие кампании массива', description: 'Заголовок карточки с идущими кампаниями массива' },
  campaignsSkeleton: { id: 'massif.detail.campaigns.skeleton', defaultMessage: 'работы на полях кампаний', description: 'Подпись для программ экрана (aria-label) пока загружается ход кампаний массива' },
  denomMassif: {
    id: 'massif.detail.denom.massif',
    defaultMessage: '{count, plural, one {работа на поле массива} few {работы на полях массива} many {работ на полях массива} other {работы на полях массива}}',
    description: 'Знаменатель полосы выполнения по массиву («Знаменатель — 312 работ на полях массива»); без числа',
  },
  denomMassifCampaign: {
    id: 'massif.detail.denom.massifCampaign',
    defaultMessage: '{count, plural, one {работа на поле массива в кампании} few {работы на полях массива в кампании} many {работ на полях массива в кампании} other {работы на полях массива в кампании}}',
    description: 'Знаменатель полосы выполнения кампании в массиве; без числа',
  },
  denomFarms: {
    id: 'massif.detail.denom.farms',
    defaultMessage: '{count, plural, one {хозяйство} few {хозяйства} many {хозяйств} other {хозяйства}}',
    description: 'Слово-знаменатель под таблицей хозяйств массива («показано 5 из 24 хозяйств»); без числа',
  },
  progressMissing: { id: 'massif.detail.campaigns.missing', defaultMessage: 'ход кампаний массива не получен: {reason}', description: 'Причина пустого блока кампаний, когда источник отказал; {reason} — причина из данных' },
  noReason: { id: 'massif.detail.campaigns.noReason', defaultMessage: 'причина не названа', description: 'Подстановка вместо причины, когда источник её не назвал' },
  noCampaignsDemo: { id: 'massif.detail.campaigns.noneDemo', defaultMessage: 'активных кампаний с работами на полях в массиве сейчас нет', description: 'Причина пустого блока кампаний при включённых демо-данных' },
  noCampaigns: { id: 'massif.detail.campaigns.none', defaultMessage: 'в срезе без демо-данных активных кампаний с работами на полях в массиве нет; синтетические кампании пилота учитываются только при включённом «Показывать демо-данные»', description: 'Причина пустого блока кампаний без демо-данных; «Показывать демо-данные» — название переключателя в настройках' },
  statusActive: { id: 'massif.detail.campaigns.active', defaultMessage: 'идёт', description: 'Бейдж у агроцикла в блоке кампаний массива: агроцикл идёт' },
  windowNote: { id: 'massif.detail.campaigns.windowNote', defaultMessage: 'часть окон ещё впереди — молчание не равно невыполнению.', description: 'Пояснение под полосой выполнения кампании массива' },
  farmsLabel: { id: 'massif.detail.farms.label', defaultMessage: 'Хозяйства массива', description: 'Название раскрывающегося блока со списком хозяйств массива' },
  farmsNote: { id: 'massif.detail.farms.note', defaultMessage: 'Хозяйства с любой долей земли в массиве, включая тех, кто делит контур с другими. Контуры и земля в строке — по всему хозяйству, а не только в этом массиве.', description: 'Пояснение в раскрытом списке хозяйств массива' },
  whatFarms: { id: 'massif.detail.what.farms', defaultMessage: 'список хозяйств массива не получен', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): список хозяйств массива' },
  farmsEmptyTitle: { id: 'massif.detail.farms.emptyTitle', defaultMessage: 'Хозяйств нет', description: 'Заголовок пустой таблицы хозяйств массива' },
  farmsEmptyReason: { id: 'massif.detail.farms.emptyReason', defaultMessage: 'в данных нет ни одного хозяйства с землёй в этом массиве', description: 'Причина пустой таблицы хозяйств массива' },
  mapTitle: { id: 'massif.detail.map.title', defaultMessage: 'Массив на карте', description: 'Заголовок карточки с картой массива' },
  mapCaption: { id: 'massif.detail.map.caption', defaultMessage: 'Только контуры этого массива', description: 'Подпись под картой массива' },
  topTitle: { id: 'massif.detail.top.title', defaultMessage: 'Больше всего земли в массиве', description: 'Заголовок блока справа от карты: хозяйства с наибольшей землёй в массиве' },
  topNone: { id: 'massif.detail.top.none', defaultMessage: 'В данных нет ни одного хозяйства с землёй в этом массиве — список строить не из чего.', description: 'Пустое состояние блока «Больше всего земли»: хозяйств нет' },
  topMissing: { id: 'massif.detail.top.missing', defaultMessage: 'Список хозяйств массива не получен — показать нечем.', description: 'Пустое состояние блока «Больше всего земли»: список не загрузился' },
  topMore: {
    id: 'massif.detail.top.more',
    defaultMessage: 'Верхние {top} из {farms, plural, one {# хозяйства} other {# хозяйств}}: полный список — раскрытием выше. Земля считается по всему хозяйству, а не только по его доле в массиве.',
    description: 'Подпись под блоком «Больше всего земли»; {top} — сколько показано (число строкой), {farms} — всего хозяйств',
  },
  topAll: { id: 'massif.detail.top.all', defaultMessage: 'Земля считается по всему хозяйству, а не только по его доле в этом массиве.', description: 'Подпись под блоком «Больше всего земли», когда показаны все хозяйства' },
  geometryTitle: { id: 'massif.detail.geometry.title', defaultMessage: 'Геометрия массива', description: 'Заголовок блока справа от карты: у скольких контуров массива есть полигон' },
  geometryNote: {
    id: 'massif.detail.geometry.note',
    defaultMessage: 'Полигон есть у {polygons} из {total, plural, one {# контура} other {# контуров}} массива. Остальные живут строкой реестра — на кадре их нет, и это пробел геометрии, а не отсутствие земли.',
    description: 'Пояснение блока «Геометрия массива»; {polygons} — у скольких есть полигон (число строкой), {total} — всего контуров',
  },
  geometryMissing: { id: 'massif.detail.geometry.missing', defaultMessage: 'Реестр контуров не прочитан: сказать, у скольких контуров есть полигон, нечем.', description: 'Пояснение блока «Геометрия массива», когда реестр контуров не загрузился' },
});

interface CycleAgg {
  total: number;
  reported: number;
  verified: number;
}

function anyLoading(...resources: { state: DataState }[]): boolean {
  return resources.some((r) => r.state === 'loading');
}

export function MassifDetail() {
  const t = useT();
  const { id = '' } = useParams();
  const massifs = useTrackData<Massif[]>(FILES.massifs);
  const contours = useTrackData<ContourMeta[]>(FILES.contours);
  // Хозяйства массива — выборка канона `/actors?massif_id=` (любая доля
  // земли в массиве), а не фильтр реестра по первому землепользователю
  // контура: тот терял хозяйства, делящие землю не первыми (см. `api/cards.ts`).
  const farms = useTrackData<Farm[]>(massifFarmsSet(id));
  const { includeDemo } = useAnalysisContext();
  const crops = useTrackData<Crop[]>(FILES.crops);
  const cycles = useTrackData<Cycle[]>(FILES.cycles);
  const progress = useTrackData<ScopeProgressFile>(scopeProgressSet('massif', id));

  const massif = (massifs.data ?? []).find((row) => row.id === id) ?? null;

  usePageTrail([
    // i18n-ignore: запасное имя района — собственное название, а не текст интерфейса
    { level: t(m.levelDistrict), name: t(m.districtName), to: '/' },
    { level: t(m.levelMassif), name: massif?.name ?? id },
  ]);

  const ownContours = useMemo(
    () => (contours.data ?? []).filter((c) => c.massif_id === id),
    [contours.data, id],
  );
  const ownIds = useMemo(() => ownContours.map((c) => c.id), [ownContours]);
  const withGeometry = useMemo(
    () => ownContours.filter((c) => c.geometry !== 'none').length,
    [ownContours],
  );

  const massifFarms = useMemo(
    () => farms.data ?? [],
    [farms.data],
  );

  /** Спутник кадра карты: верх списка по земле, бюджет первого взгляда. */
  const topFarms = useMemo(
    () => [...massifFarms].sort((a, b) => b.area_ha - a.area_ha).slice(0, DENSITY.listItems),
    [massifFarms],
  );

  /**
   * Идущие кампании массива: цикл из среза, числа — из агрегата сервера по
   * этому же циклу и массиву. Цикл без строки хода в карточку не попадает:
   * показать его с нулями значило бы сказать «обязательств нет» там, где
   * сервер про них ничего не ответил.
   */
  const activeCampaigns = useMemo(() => {
    const byCycle = new Map((progress.data?.cycles ?? []).map((row) => [row.cycle, row]));
    const entries: { cycle: Cycle; agg: CycleAgg }[] = [];
    for (const cycle of cycles.data ?? []) {
      if (cycle.status !== 'active') continue;
      const row = byCycle.get(cycle.id);
      if (!row) continue;
      entries.push({
        cycle,
        agg: {
          total: row.obligations.total,
          reported: row.obligations.reported,
          verified: row.obligations.verified,
        },
      });
    }
    return entries;
  }, [cycles.data, progress.data]);

  const campaignsLoading = anyLoading(cycles, progress);

  const farmColumns: Column<Farm>[] = [
    {
      key: 'name',
      header: t(m.colFarm),
      render: (row) => <Link to={`/farms/${row.id}`}>{row.name}</Link>,
    },
    { key: 'direction', header: t(m.colDirection), render: (row) => row.direction },
    { key: 'contours', header: t(m.colContours), numeric: true, render: (row) => num(row.contours) },
    { key: 'area', header: t(m.colLand), unit: t(UNIT_LABELS.ha), numeric: true, render: (row) => ha(row.area_ha) },
  ];

  return (
    <Stack>
      <PageHeader
        level="massif"
        title={massif ? t(m.titleNamed, { name: massif.name }) : t(m.titleFallback, { id })}
        purpose={massif ? t(m.purpose) : t(m.purposeMissing)}
      />

      <div data-guide="kpi"><Grid cols={4}>
        <KpiCard
          label={t(m.kpiContours)}
          value={massif ? num(massif.contours) : '—'}
          note={contours.state === 'ready' ? t(m.kpiContoursNote, { count: num(ownContours.length) }) : t(m.kpiContoursNoteMissing)}
        />
        <KpiCard
          label={t(m.kpiFarms)}
          value={farms.state === 'ready' ? num(massifFarms.length) : '—'}
          note={massif ? t(m.kpiFarmsNote, { count: num(massif.farms) }) : t(m.kpiFarmsNoteShort)}
        />
        <KpiCard
          label={t(m.kpiLand)}
          value={massif ? ha(massif.area_ha) : '—'}
          unit={t(UNIT_LABELS.ha)}
          note={t(m.kpiLandNote)}
        />
        <KpiCard
          label={t(m.kpiGeometry)}
          value={contours.state === 'ready' ? num(withGeometry) : '—'}
          note={contours.state === 'ready' ? t(m.kpiGeometryNote, { count: ownContours.length }) : t(m.kpiContoursNoteMissing)}
        />
      </Grid></div>

      <div data-guide="campaigns"><Card size="lg" title={t(m.campaignsTitle)}>
        {campaignsLoading ? (
          <SkeletonList label={t(m.campaignsSkeleton)} items={2} tone="soft" />
        ) : activeCampaigns.length === 0 ? (
          <DoneSplit
            counts={null}
            denominator={m.denomMassif}
            reason={
              // Отказ источника и настоящее отсутствие кампаний звучат
              // по-разному: «нет данных» — не то же самое, что «ноль».
              progress.state === 'missing'
                ? t(m.progressMissing, { reason: progress.reason ?? t(m.noReason) })
                : includeDemo
                  ? t(m.noCampaignsDemo)
                  : t(m.noCampaigns)
            }
          />
        ) : (
          <Stack tight>
            {activeCampaigns.map(({ cycle, agg }) => (
              <div className="cyclecard" key={cycle.id}>
                <div className="cyclecard__row">
                  <span className="cyclecard__name">
                    <Link to={`/cycles/${cycle.id}`}>{cycleTitle(cycle, crops.data)}</Link>
                  </span>
                  <Badge tone="sage">{t(m.statusActive)}</Badge>
                </div>
                <DoneSplit
                  counts={{ total: agg.total, reported: agg.reported, verified: agg.verified }}
                  denominator={m.denomMassifCampaign}
                  windowNote={t(m.windowNote)}
                />
              </div>
            ))}
          </Stack>
        )}
      </Card></div>

      <div data-guide="farms"><Stack tight>
        <Disclosure label={t(m.farmsLabel)} summary={countOf(massifFarms.length, WORDS.farm)}>
          <p className="t-caption" style={{ marginBottom: 'var(--sp-3)' }}>
            {t(m.farmsNote)}
          </p>
          <DataGate res={farms} what={t(m.whatFarms)}>
            {() => (
              <DataTable
                columns={farmColumns}
                rows={massifFarms}
                getKey={(row) => row.id}
                denominator={m.denomFarms}
                empty={{
                  title: t(m.farmsEmptyTitle),
                  reason: t(m.farmsEmptyReason),
                }}
              />
            )}
          </DataGate>
        </Disclosure>
      </Stack></div>

      <div data-guide="map"><Card size="lg" title={t(m.mapTitle)}>
        <div className="mapsplit">
          <div className="mapsplit__frame">
            <MapWidget
              colorBy="crop"
              crops={crops.data ?? undefined}
              mode="frame"
              height="100%"
              filterIds={ownIds.length > 0 ? ownIds : undefined}
              focusIds={ownIds.length > 0 ? ownIds : undefined}
              caption={t(m.mapCaption)}
            />
          </div>

          <div className="mapsplit__aside">
            <div className="mapaside__block">
              <div className="mapaside__title">{t(m.topTitle)}</div>
              {topFarms.length === 0 ? (
                <div className="mapaside__note">
                  {farms.state === 'ready' ? t(m.topNone) : t(m.topMissing)}
                </div>
              ) : (
                <>
                  <div className="toplist toplist--nodot">
                    {topFarms.map((farm) => (
                      <div className="toplist__row" key={farm.id}>
                        <span className="toplist__name">
                          <Link to={`/farms/${farm.id}`}>{farm.name}</Link>
                        </span>
                        <span className="toplist__val">{haUnit(farm.area_ha)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mapaside__note">
                    {massifFarms.length > topFarms.length
                      ? t(m.topMore, { top: num(topFarms.length), farms: massifFarms.length })
                      : t(m.topAll)}
                  </div>
                </>
              )}
            </div>

            <div className="mapaside__block">
              <div className="mapaside__title">{t(m.geometryTitle)}</div>
              <div className="mapaside__note">
                {contours.state === 'ready'
                  ? t(m.geometryNote, { polygons: num(withGeometry), total: ownContours.length })
                  : t(m.geometryMissing)}
              </div>
            </div>
          </div>
        </div>
      </Card></div>
    </Stack>
  );
}
